/** Navigation and history signals that the PM thread view reads (#4668). */

// WebKit cancels in-flight requests when a same-tab link navigation starts, before
// pagehide. A link that never unloads the page delays a cancelled load's error this long.
export const PM_LINK_NAVIGATION_WINDOW_MS = 2000;

/**
 * Whether a click navigates this tab to another document: a plain primary click
 * on an http(s) link that no handler prevented, with no target or download.
 *
 * @param {MouseEvent} event a click whose dispatch is complete
 * @param {string} pageHref the address of this document
 * @returns {boolean}
 */
export function pmIsSameTabLinkNavigation(event, pageHref) {
    if (!event || event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

    const link = event.target && typeof event.target.closest === 'function'
        ? event.target.closest('a[href]')
        : null;
    if (!link) return false;

    const target = link.getAttribute('target');
    if ((target && target !== '_self') || link.hasAttribute('download')) return false;
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return false;

    // A fragment link to this document scrolls and does not unload the page.
    const linkUrl = String(link.href).split('#');
    return !(linkUrl.length > 1 && linkUrl[0] === String(pageHref).split('#')[0]);
}

/**
 * Records the last click on `browserWindow`.
 *
 * @returns {{started: function(): boolean}} whether that click started a same-tab
 *   link navigation inside the window
 */
export function createPmLinkNavigationTracker(browserWindow) {
    let lastClick = null;

    // Capture phase, so a handler that stops propagation cannot hide the click. The
    // click is judged when asked, after every handler had its chance to prevent it.
    browserWindow.addEventListener('click', (event) => {
        lastClick = { event, at: Date.now() };
    }, true);
    // A restored page does not carry a navigation from before it left.
    browserWindow.addEventListener('pageshow', () => {
        lastClick = null;
    });

    return {
        started: () => lastClick !== null
            && Date.now() - lastClick.at < PM_LINK_NAVIGATION_WINDOW_MS
            && pmIsSameTabLinkNavigation(lastClick.event, browserWindow.location.href),
    };
}

/**
 * What a history entry does to the conversation pane: 'open', 'keep' or 'close'.
 * A server-rendered entry has no state, and its URL still names the conversation.
 *
 * @param {{state: *, urlThreadId: *, urlThreadListed: boolean, openThreadId: *}} entry
 *   state is history.state, urlThreadId the thread_id in the URL, urlThreadListed
 *   whether the thread list shows a row for it, and openThreadId the conversation in
 *   the pane. An absent id is any falsy value.
 * @returns {{action: string, threadId: ?string}}
 */
export function pmPopstateAction({ state, urlThreadId, urlThreadListed, openThreadId }) {
    if (state && typeof state.thread_id !== 'undefined' && state.thread_id !== '' && !isNaN(state.thread_id)) {
        return { action: 'open', threadId: String(state.thread_id) };
    }
    if (!urlThreadId) return { action: 'close', threadId: null };

    // A fragment link adds a stateless entry for the conversation that is already open.
    if (openThreadId && String(openThreadId) === String(urlThreadId)) {
        return { action: 'keep', threadId: String(urlThreadId) };
    }
    // A message request shows no row, and view_thread would open it without its request bar.
    if (!urlThreadListed) return { action: 'close', threadId: null };

    return { action: 'open', threadId: String(urlThreadId) };
}

// history.state key of every entry made while the Message requests view is open.
// Its value is the entry's depth: 1 for the entry the view pushes when it opens,
// and one more for each entry pushed on top of it.
export const PM_REQUESTS_VIEW_STATE_KEY = 'pm_requests_view';

/**
 * How many entries the Message requests view owns, up to and including this one.
 *
 * @param {*} state history.state
 * @returns {number} 0 when the entry is not the view's
 */
export function pmRequestsViewDepth(state) {
    const depth = state && typeof state === 'object' ? state[PM_REQUESTS_VIEW_STATE_KEY] : 0;

    return Number.isInteger(depth) && depth > 0 ? depth : 0;
}

/**
 * A history.state that belongs to the Message requests view.
 *
 * @param {?Object} base the state the entry carries for the thread pane
 * @param {number} depth 1 for the view's own entry
 * @returns {Object}
 */
export function pmRequestsViewEntry(base, depth) {
    return { ...(base || {}), [PM_REQUESTS_VIEW_STATE_KEY]: depth };
}

/**
 * @param {*} state history.state of a Message requests view entry
 * @returns {?Object} the state without the view's marker, null when nothing is left
 */
export function pmWithoutRequestsView(state) {
    const rest = { ...state };
    delete rest[PM_REQUESTS_VIEW_STATE_KEY];

    return Object.keys(rest).length ? rest : null;
}

/**
 * What a history entry does to the Message requests view: 'open', 'close' or 'keep'.
 *
 * @param {{state: *, overlayOpen: boolean}} entry state is history.state, and
 *   overlayOpen whether the view is on screen
 * @returns {string}
 */
export function pmRequestsViewAction({ state, overlayOpen }) {
    const wanted = pmRequestsViewDepth(state) > 0;
    if (wanted === !!overlayOpen) return 'keep';

    return wanted ? 'open' : 'close';
}

/**
 * What a popstate in the chat view does, once the Message requests view has
 * synced with the entry.
 *
 * @param {{state: *, requestsView: ?string, url: string, urlThreadId: *, urlThreadListed: boolean, openThreadId: *}} step
 *   requestsView is what PmRequests.syncWithHistory applied ('open', 'close',
 *   'keep', 'leave' or 'close-request'), and url the
 *   address of the entry. The rest is as pmPopstateAction takes it.
 * @returns {{action: string, threadId: ?string, replace: ?{state: ?Object, url: string}}}
 *   action and threadId for the conversation pane, and replace for the current entry
 */
export function pmPopstateStep({ state, requestsView, url, urlThreadId, urlThreadListed, openThreadId }) {
    const openId = openThreadId ? String(openThreadId) : null;
    // The in-view Back closes the list only. The conversation on screen stays,
    // and the entry the step landed on is rewritten to name it.
    if (requestsView === 'leave') {
        if ((urlThreadId ? String(urlThreadId) : null) === openId) {
            return { action: 'keep', threadId: openId, replace: null };
        }
        const address = new URL(url, 'https://host.invalid');
        if (openId) address.searchParams.set('thread_id', openId);
        else address.searchParams.delete('thread_id');
        return {
            action: 'keep',
            threadId: openId,
            replace: { state: openId ? { thread_id: openId } : null, url: address.pathname + address.search + address.hash },
        };
    }
    // A step back off a request opened from the view shows the list, whatever
    // conversation the landing entry names.
    if (requestsView === 'close-request') {
        const address = new URL(url, 'https://host.invalid');
        const named = address.searchParams.has('thread_id') || !!(state && 'thread_id' in state);
        if (!named) return { action: 'close', threadId: null, replace: null };
        const rest = { ...state };
        delete rest.thread_id;
        address.searchParams.delete('thread_id');
        return {
            action: 'close',
            threadId: null,
            replace: { state: rest, url: address.pathname + address.search + address.hash },
        };
    }
    const entry = pmPopstateAction({ state, urlThreadId, urlThreadListed, openThreadId });
    // A step onto or off the view keeps the conversation that is already open.
    if ((requestsView === 'open' || requestsView === 'close') && entry.action === 'open' && entry.threadId === openId) {
        return { action: 'keep', threadId: openId, replace: null };
    }

    return { ...entry, replace: null };
}

/**
 * Whether a photo anchor's href names a profile.
 *
 * The href must name a path segment under the root. PmViewBuilder writes '#'
 * where there is no profile to open. A path normalizes a backslash to a
 * slash, so the class excludes both slash forms.
 *
 * @param {?string} href the href attribute of the photo anchor
 * @returns {boolean}
 */
export function pmIsProfileHref(href) {
    return typeof href === 'string' && /^\/[^/\\?#]/.test(href);
}

/**
 * What a click inside a thread row does.
 *
 * The drag reading precedes the photo, because swipe-actions.js holds
 * isDragging past touchend.
 *
 * @param {{isIcon: boolean, inButton: boolean, inRowActions: boolean,
 *   isDragging: boolean, avatarHref: ?string}} target where the click landed.
 *   avatarHref is the href attribute of the photo anchor around it, and null
 *   when the click is outside the photo.
 * @returns {string} 'link' leaves the click to the browser, 'ignore' cancels
 *   the click, and 'open' opens the conversation.
 */
export function pmThreadRowClickAction({ isIcon, inButton, inRowActions, isDragging, avatarHref }) {
    if (isIcon) return 'ignore';
    if (inButton || inRowActions) return 'ignore';
    if (isDragging) return 'ignore';
    if (pmIsProfileHref(avatarHref)) return 'link';

    return 'open';
}
